import { useContext, useEffect, useState } from 'react';
import Modal from 'components/ui/Modal';
import Icon from 'components/ui/Icon';
import Button from 'components/ui/Button';
import { LocalizationContext } from 'context/localization.context';
import { getProshopDrafts, ProshopDraftModel } from 'api/proshop';
import { OpenProshopDraft } from 'store/modules/cart';
import { FetchUser, selectUserObject } from 'store/modules/user';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AlertModal from 'components/ui/AlertModal';
import {
	ServerError,
	GenericWarning,
	ProshopCartTakeoverError,
	ProshopCartEmptyError,
} from 'common/helpers';

interface Props {
	url: string;
	onToggle: () => void;
}

const ProshopDraftsModal = ({ url, onToggle }: Props) => {
	const { t }: any = useContext(LocalizationContext);
	const [drafts, setDrafts] = useState<ProshopDraftModel[]>([]);
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<{
		message: string;
		onConfirm: () => void;
	} | null>(null);

	const dispatch = useDispatch();
	const { customers } = useSelector(selectUserObject);
	const selectedCustomer = customers?.filter(
		(item: any) => item.selected === true
	)[0];

	// Function to handle opening a draft, with error handling for various scenarios.
	// If the cart is taken over by another user or is empty, it prompts the user with an alert to confirm if they want to proceed with opening the draft.
	const handleOpenDraft = async (url: string) => {
		if (!url) return;
		try {
			await dispatch(OpenProshopDraft(url));
			// Refresh the customer dropdown, since opening a draft can change selected customer.
			if (selectedCustomer?.id) {
				dispatch(FetchUser(selectedCustomer.id));
			}
			toast(t('cartpage/proshop/cartopendsuccessfully'), {
				type: toast.TYPE.SUCCESS,
			});
			onToggle && onToggle();
		} catch (err) {
			if (err instanceof ServerError) {
				toast(t('shared/toast/servererror'), { type: toast.TYPE.ERROR });
			} else if (err instanceof ProshopCartTakeoverError) {
				const urlObj = new URL(url, window.location.origin);
				urlObj.searchParams.set('takeOver', 'true');
				setAlert({
					message: err.message,
					onConfirm: () => handleOpenDraft(urlObj.toString()),
				});
			} else if (err instanceof ProshopCartEmptyError) {
				const urlObj = new URL(url, window.location.origin);
				urlObj.searchParams.set('emptyCart', 'true');
				setAlert({
					message: err.message,
					onConfirm: () => handleOpenDraft(urlObj.toString()),
				});
			} else if (err instanceof GenericWarning) {
				toast(err.message, { type: toast.TYPE.WARNING });
			}
		}
	};

	useEffect(() => {
		setLoading(true);
		getProshopDrafts(url)
			.then((data) => {
				if (data) setDrafts(data);
			})
			.finally(() => setLoading(false));
	}, [url]);

	return (
		<>
			<Modal
				toggleModal={onToggle}
				heading={t('cartpage/proshop/drafts')}
				title="Proshop Drafts"
				width="large"
			>
				{loading ? (
					<div className="flex justify-center py-8">
						<Icon icon="loader" animate="spin" size={5} aria-hidden={true} />
					</div>
				) : drafts.length === 0 ? (
					<p className="text-p py-4">{t('cartpage/proshop/nodrafts')}</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full quickimporttable">
							<caption></caption>
							<thead className="bg-blue">
								<tr className="text-white text-p">
									<th className="text-sm p-1 md:text-p md:py-2 md:px-4 font-medium text-left whitespace-no-wrap">
										{t('cartpage/proshop/orderid')}
									</th>
									<th className="text-sm p-1 md:text-p md:py-2 md:px-4 font-medium text-left whitespace-no-wrap">
										{t('cartpage/proshop/orgname')}
									</th>
									<th className="text-sm p-1 md:text-p md:py-2 md:px-4 font-medium text-left whitespace-no-wrap">
										{t('cartpage/proshop/user')}
									</th>
									<th className="text-sm p-1 md:text-p md:py-2 md:px-4 font-medium text-left whitespace-no-wrap">
										{t('cartpage/proshop/modified')}
									</th>
									<th className="text-sm p-1 md:text-p md:py-2 md:px-4 font-medium text-left whitespace-no-wrap"></th>
								</tr>
							</thead>
							<tbody>
								{drafts.map((draft, index) => (
									<tr key={index} className="border-b border-greyLight">
										<td className="text-p py-2 px-4">{draft.orderId}</td>
										<td className="text-p py-2 px-4">{draft.orgName}</td>
										<td className="text-p py-2 px-4">{draft.user}</td>
										<td className="text-p py-2 px-4">
											{new Date(draft.modified).toLocaleDateString()}
										</td>
										{draft.openAsCartUrl && (
											<td className="py-2 px-4">
												<Button
													type="button"
													buttonColor="blue"
													onClick={(e) => {
														e.preventDefault();
														handleOpenDraft(draft.openAsCartUrl);
													}}
												>
													{t('cartpage/proshop/opencart')}
													<Icon
														icon="chevrons"
														direction="left"
														size={1.25}
														aria-hidden="true"
													/>
												</Button>
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Modal>
			{alert && (
				<AlertModal
					title={t('cartpage/proshop/drafts')}
					heading={t('cartpage/proshop/drafts')}
					message={alert.message}
					cancelButtonText={t('shared/general/cancel')}
					actionButtonText={t('shared/general/yes')}
					onToggle={() => setAlert(null)}
					onClick={() => {
						alert.onConfirm();
						setAlert(null);
					}}
				/>
			)}
		</>
	);
};

export default ProshopDraftsModal;
